import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import TraderMatch from '../../common/TraderMatch/TraderMatch';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import './Dashboard.css';

/**
 * THE TRADE DESK - the logged-in home page, action first.
 *
 *   Header      the standard page header + ONE number (binder value)
 *   Up next     every trade that needs YOU, as a card: who, the cards
 *               involved, what to do, one button. Trades waiting on
 *               the other side collapse to a single line under it.
 *   Available   wishlist cards someone owns right now, as card art
 *               (opens Search on that card, owned printings only)
 *   For you     the best binder fits, as the shared match cards
 *
 * Refreshes itself every 15s (and on focus) like the Trades page.
 */

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

// Copies collapse into one thumb with a ×N badge
const groupCards = (cards) => {
    const seen = new Map();
    (cards || []).forEach(c => {
        const key = `${c.uuid}|${c.finish}`;
        if (seen.has(key)) seen.get(key).count++;
        else seen.set(key, { ...c, count: 1 });
    });
    return [...seen.values()];
};

function Thumbs({ label, cards }) {
    const list = groupCards(cards);
    if (!list.length) return null;
    return (
        <div className="desk-side">
            <span className="desk-side-label">{label}</span>
            <span className="desk-thumbs">
                {list.slice(0, 5).map((c, i) => (
                    <span key={`${c.uuid}-${i}`} className="desk-thumb" title={c.name}>
                        {c.image_path ? <img src={c.image_path} alt={c.name} loading="lazy" /> : <span className="desk-thumb-none">{c.name}</span>}
                        {c.count > 1 && <span className="desk-thumb-count">×{c.count}</span>}
                    </span>
                ))}
                {list.length > 5 && <span className="desk-more">+{list.length - 5}</span>}
            </span>
        </div>
    );
}

function Dashboard({ user }) {
    const navigate = useNavigate();
    const [binders, setBinders] = useState(null);
    const [trades, setTrades] = useState(null);
    const [stages, setStages] = useState({});
    const [partners, setPartners] = useState({});
    const [fits, setFits] = useState(null);
    const [available, setAvailable] = useState(null);

    const loadTrades = useCallback(() => {
        const opts = { credentials: 'include' };
        fetch('/api/trade-requests?type=dashboard', opts)
            .then(r => (r.ok ? r.json() : null))
            .then(d => setTrades(d?.requests || []))
            .catch(() => setTrades(prev => prev || []));
        fetch('/api/trade-requests/shipping-attention', opts)
            .then(r => (r.ok ? r.json() : null))
            .then(d => {
                const map = {};
                (d?.items || []).forEach(i => { map[i.tradeId] = i; });
                setStages(map);
            })
            .catch(() => { });
    }, []);

    useEffect(() => {
        const opts = { credentials: 'include' };
        fetch(`/api/users/${user.id}/binders`, opts)
            .then(r => (r.ok ? r.json() : null))
            .then(d => setBinders(d || { haves: [], wants: [] }))
            .catch(() => setBinders({ haves: [], wants: [] }));
        fetch('/api/suggested/users', opts)
            .then(r => (r.ok ? r.json() : null))
            .then(d => setFits(d?.users || []))
            .catch(() => setFits([]));
        // The feed ranks "cards you want that someone owns" first
        fetch('/api/onesearch/feed', opts)
            .then(r => (r.ok ? r.json() : null))
            .then(d => setAvailable((d?.cards || []).filter(c => c.onWishlist && c.counts?.have > 0)))
            .catch(() => setAvailable([]));
        loadTrades();

        const id = setInterval(() => { if (!document.hidden) loadTrades(); }, 15000);
        window.addEventListener('focus', loadTrades);
        return () => {
            clearInterval(id);
            window.removeEventListener('focus', loadTrades);
        };
    }, [user.id, loadTrades]);

    // ---- whose move is it? (same grouping as the Trades page) ----
    const list = trades || [];
    const incoming = list.filter(r => r.status === 'pending' && r.otherId === user.id);
    const outgoing = list.filter(r => r.status === 'pending' && r.ownerId === user.id);
    const active = list.filter(r => ['accepted', 'processing'].includes(r.status));
    const yourTurn = [
        ...incoming.map(r => ({ r, kind: 'offer' })),
        ...active.filter(r => stages[r.tradeId]?.actionNeeded).map(r => ({ r, kind: 'ship' })),
    ];
    const waitingCount = outgoing.length + active.filter(r => !stages[r.tradeId]?.actionNeeded).length;

    // Faces for the trades on screen
    const partnerOf = (r) => (r.ownerId === user.id ? r.otherId : r.ownerId);
    const partnerKey = yourTurn.map(({ r }) => partnerOf(r)).filter(Boolean).join(',');
    useEffect(() => {
        partnerKey.split(',').filter(Boolean).forEach(id => {
            if (partners[id]) return;
            fetch(`/api/users/${id}/public`)
                .then(r => (r.ok ? r.json() : null))
                .then(d => { if (d?.user) setPartners(p => ({ ...p, [id]: d.user })); })
                .catch(() => { });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partnerKey]);

    const binderValue = binders
        ? binders.haves.reduce((sum, { card, entry }) => sum + (card.sortPrice || 0) * (entry.quantity || 1), 0)
        : null;

    const upNextCard = ({ r, kind }) => {
        const iAmOther = r.otherId === user.id;
        const getCards = iAmOther ? r.ownerTrade : r.otherTrade;
        const giveCards = iAmOther ? r.otherTrade : r.ownerTrade;
        const pid = partnerOf(r);
        const partner = partners[pid];
        const stage = stages[r.tradeId];
        const title = kind === 'offer' ? 'New offer for you' : (stage?.label || 'Your turn');
        const go = kind === 'offer' ? '/requests' : `/shipping/${r.tradeId}`;
        return (
            <article key={`${kind}-${r.tradeId}`} className="desk-next">
                <header className="desk-next-head">
                    <button className="desk-who" onClick={() => pid && navigate(`/profile/${pid}`)}>
                        <AvatarFrame user={partner}>
                            <img
                                src={partner ? getProfilePhotoSrc(partner) : PLACEHOLDER_DATA_URI}
                                alt=""
                                onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
                            />
                        </AvatarFrame>
                        <span className="desk-who-name">{partner?.username || '…'}</span>
                    </button>
                    {r.trade_code && <span className="desk-code">{r.trade_code}</span>}
                </header>
                <h3 className="desk-next-title">{title}</h3>
                <div className="desk-sides">
                    <Thumbs label="You get" cards={getCards} />
                    <Thumbs label="You give" cards={giveCards} />
                </div>
                <div className="desk-next-actions">
                    <button className="cta-gold" onClick={() => navigate(go)}>
                        {kind === 'offer' ? 'Review offer' : 'Open'}
                    </button>
                </div>
            </article>
        );
    };

    return (
        <div className="home-shell desk">
            <header className="page-section-head desk-head">
                <h1>Home <span className="page-section-current">{user.username}</span></h1>
                <button className="desk-worth" onClick={() => navigate('/binders')} title="Your collection's estimated value">
                    <span>Binder</span>
                    <strong>{binderValue === null ? '…' : usd.format(binderValue)}</strong>
                </button>
            </header>

            {/* ---- Up next: what needs you ---- */}
            <section className="desk-sec">
                <div className="desk-sec-head">
                    <h2>Up next</h2>
                    <Link to="/requests" className="desk-link">All trades →</Link>
                </div>
                {trades === null ? (
                    <p className="desk-empty">Loading&hellip;</p>
                ) : yourTurn.length === 0 ? (
                    <div className="desk-clear">
                        <strong>You're all caught up.</strong>
                        <span>
                            {waitingCount > 0
                                ? `${waitingCount} trade${waitingCount !== 1 ? 's are' : ' is'} waiting on the other side.`
                                : 'Start a trade from the cards and traders below.'}
                        </span>
                    </div>
                ) : (
                    <>
                        <div className="desk-next-grid">{yourTurn.map(upNextCard)}</div>
                        {waitingCount > 0 && (
                            <p className="desk-waiting">
                                Plus {waitingCount} waiting on the other side · <Link to="/requests">see them</Link>
                            </p>
                        )}
                    </>
                )}
            </section>

            {/* ---- Wanted cards someone owns right now ---- */}
            {available && available.length > 0 && (
                <section className="desk-sec">
                    <div className="desk-sec-head">
                        <h2>Wanted cards, available now</h2>
                        <Link to="/search?avail=have" className="desk-link">Browse all →</Link>
                    </div>
                    <div className="desk-avail">
                        {available.slice(0, 8).map(c => (
                            <button
                                key={c.name}
                                className="desk-avail-card"
                                onClick={() => navigate(`/search?card=${encodeURIComponent(c.name)}&avail=have`)}
                                title={`See who has ${c.name}`}
                            >
                                {c.image_path
                                    ? <img src={c.image_path} alt={c.name} loading="lazy" />
                                    : <span className="desk-thumb-none">{c.name}</span>}
                                <span className="desk-avail-name">{c.name}</span>
                                <span className="desk-avail-have">{c.counts.have} {c.counts.have === 1 ? 'trader has' : 'traders have'} it</span>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {/* ---- Best binder fits ---- */}
            <section className="desk-sec">
                <div className="desk-sec-head">
                    <h2>Traders for you</h2>
                    <Link to="/traders" className="desk-link">See all →</Link>
                </div>
                {fits === null ? (
                    <p className="desk-empty">Loading&hellip;</p>
                ) : fits.length === 0 ? (
                    <p className="desk-empty">
                        No fits yet. Add cards to your <Link to="/binders">binder and wishlist</Link> and
                        traders whose cards line up will show here.
                    </p>
                ) : (
                    <div className="os-match-grid">
                        {fits.slice(0, 3).map(m => <TraderMatch key={m.id} m={m} />)}
                    </div>
                )}
            </section>
        </div>
    );
}

export default Dashboard;
